import { z } from 'zod';

// ─── Response schemas ─────────────────────────────────────────────────────────

export const headerViewSchema = z.object({
  alg: z.string(),
  typ: z.string().nullable(),
  kid: z.string().nullable(),
});

export const payloadViewSchema = z.object({
  sub: z.string().nullable(),
  iss: z.string().nullable(),
  aud: z.string().nullable(),
  jti: z.string().nullable(),
  userId: z.number().nullable(),
  username: z.string().nullable(),
  roles: z.array(z.string()),
  permissions: z.array(z.string()),
  iat: z.string().nullable(),
  exp: z.string().nullable(),
});

export const signatureMetaViewSchema = z.object({
  algorithm: z.string(),
  issuer: z.string().nullable(),
});

export const decodedTokenViewSchema = z.object({
  header: headerViewSchema,
  payload: payloadViewSchema,
  signatureMeta: signatureMetaViewSchema,
  signatureValid: z.boolean(),
  expired: z.boolean(),
  blacklisted: z.boolean().nullable(),
  remainingTtlSeconds: z.number().nullable(),
  dbPermissions: z.array(z.string()),
  claimVsDbDiff: z.array(z.string()),
  maskedToken: z.string(),
});

export const blacklistStatusViewSchema = z.object({
  jti: z.string(),
  redisHit: z.boolean(),
  jpaHit: z.boolean(),
  revokedAt: z.string().nullable(),
  revokedReason: z.string().nullable(),
  expiresAt: z.string().nullable(),
});

export const refreshItemStatusSchema = z.enum(['ACTIVE', 'ROTATED', 'REVOKED', 'EXPIRED']);

export const refreshRotationItemSchema = z.object({
  jti: z.string(),
  issuedAt: z.string(),
  expiresAt: z.string(),
  rotatedAt: z.string().nullable(),
  blacklistedAt: z.string().nullable(),
  status: refreshItemStatusSchema,
});

export const refreshHistoryViewSchema = z.object({
  userId: z.number(),
  items: z.array(refreshRotationItemSchema),
});

// ─── Form schemas ─────────────────────────────────────────────────────────────

/**
 * JWT format: 3 base64url segments separated by dots.
 * Signature segment may be empty (unsecured JWTs), hence the trailing * not +.
 */
export const decodeFormSchema = z.object({
  token: z
    .string()
    .min(1, 'JWT 토큰을 입력해 주세요')
    .max(4096, '토큰이 너무 깁니다 (최대 4096자)')
    .regex(
      /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*$/,
      'JWT 형식이 아닙니다 (header.payload.signature)'
    ),
});
export type DecodeFormValues = z.infer<typeof decodeFormSchema>;

export const blacklistFormSchema = z.object({
  jti: z.string().min(1, 'jti를 입력해 주세요'),
});
export type BlacklistFormValues = z.infer<typeof blacklistFormSchema>;

export const historyFormSchema = z.object({
  userId: z
    .string()
    .min(1, 'userId를 입력해 주세요')
    .regex(/^\d+$/, '숫자만 입력해 주세요'),
});
export type HistoryFormValues = z.infer<typeof historyFormSchema>;
